import { t } from "@/lib/i18n"
import { cn } from "@/lib/utils"

import { LandingSection, SectionRule, SectionTitle } from "./section-rule"

/**
 * The staff panel shows the floor attention worklist, which is what
 * `Staff::FloorAttentionQuery` actually serves: one row per (table, reason),
 * ranked by priority and carrying the age of each item. Its controls render as
 * static text rather than buttons — this is a picture of the product, and the
 * honest-UI rule forbids a control that does nothing.
 */
const attentionRows = [
  { key: "entry", tone: "bg-status-reserved" },
  { key: "call", tone: "bg-status-occupied" },
  { key: "ready", tone: "bg-status-occupied" },
]

const billRows = [
  { key: "aguachile", mine: true },
  { key: "jamaica", mine: true },
  { key: "other", mine: false },
]

export function SurfacesSection() {
  return (
    <LandingSection id="audience">
      <SectionRule>{t("ui.landing.surfaces.label")}</SectionRule>
      <SectionTitle className="mt-6 max-w-[18em]">{t("ui.landing.surfaces.title")}</SectionTitle>

      <div className="mt-12 grid items-stretch gap-4 lg:grid-cols-[1.25fr_1fr]">
        <article className="flex flex-col overflow-hidden rounded-xl border bg-card">
          <div className="flex flex-col gap-2 p-6 pb-5">
            <span className="font-mono text-xs text-muted-foreground">{t("ui.landing.surfaces.staff.host")}</span>
            <h3 className="text-xl font-semibold tracking-tight">{t("ui.landing.surfaces.staff.title")}</h3>
            <p className="text-sm leading-6 text-muted-foreground">{t("ui.landing.surfaces.staff.description")}</p>
          </div>

          <div className="mx-6 mb-6 overflow-hidden rounded-lg border">
            <div className="flex items-center justify-between border-b bg-muted px-3.5 py-2.5">
              <span className="text-sm font-semibold">{t("ui.landing.surfaces.attention.title")}</span>
              <span className="text-xs font-medium tabular-nums text-muted-foreground">
                {t("ui.landing.surfaces.attention.count")}
              </span>
            </div>
            {attentionRows.map((row, index) => (
              <div
                key={row.key}
                className={cn(
                  "grid grid-cols-[0.625rem_minmax(0,1fr)_auto_auto] items-center gap-3 px-3.5 py-3",
                  index < attentionRows.length - 1 && "border-b"
                )}
              >
                <span className={cn("size-2 rounded-full", row.tone)} aria-hidden />
                <span className="flex flex-col gap-0.5">
                  <span className="text-sm font-medium">{t(`ui.landing.surfaces.attention.rows.${row.key}.title`)}</span>
                  <span className="text-xs text-muted-foreground">
                    {t(`ui.landing.surfaces.attention.rows.${row.key}.detail`)}
                  </span>
                </span>
                <span className="text-sm font-medium tabular-nums text-muted-foreground">
                  {t(`ui.landing.surfaces.attention.rows.${row.key}.age`)}
                </span>
                <span
                  className={cn(
                    "inline-flex h-7 items-center rounded-md px-3 text-xs font-medium",
                    index === 0 ? "bg-primary text-primary-foreground" : "border"
                  )}
                >
                  {t(`ui.landing.surfaces.attention.rows.${row.key}.action`)}
                </span>
              </div>
            ))}
          </div>
        </article>

        <article className="flex flex-col overflow-hidden rounded-xl border bg-card">
          <div className="flex flex-col gap-2 p-6 pb-5">
            <span className="font-mono text-xs text-muted-foreground">{t("ui.landing.surfaces.user.host")}</span>
            <h3 className="text-xl font-semibold tracking-tight">{t("ui.landing.surfaces.user.title")}</h3>
            <p className="text-sm leading-6 text-muted-foreground">{t("ui.landing.surfaces.user.description")}</p>
          </div>

          <div className="mx-6 mb-6 overflow-hidden rounded-lg border">
            <div className="flex items-center justify-between border-b bg-muted px-3.5 py-2.5">
              <span className="text-sm font-semibold">{t("ui.landing.surfaces.bill.title")}</span>
              <span className="inline-flex items-center rounded-full bg-brand-solid px-2 py-0.5 text-xs font-medium text-brand-foreground">
                {t("ui.landing.surfaces.bill.mine")}
              </span>
            </div>
            {billRows.map((row) => (
              <div
                key={row.key}
                className={cn("flex items-center justify-between gap-3 border-b px-3.5 py-3", row.mine && "bg-brand-subtle")}
              >
                <span className={cn("text-sm", row.mine ? "font-medium" : "text-muted-foreground")}>
                  {t(`ui.landing.surfaces.bill.items.${row.key}.name`)}
                </span>
                <span className={cn("text-sm tabular-nums", row.mine ? "font-semibold" : "text-muted-foreground")}>
                  {t(`ui.landing.surfaces.bill.items.${row.key}.amount`)}
                </span>
              </div>
            ))}
            <div className="flex flex-col gap-3 p-3.5">
              <div className="flex items-baseline justify-between">
                <span className="text-sm font-semibold">{t("ui.landing.surfaces.bill.due_label")}</span>
                <span className="text-lg font-bold tracking-tight tabular-nums">
                  {t("ui.landing.surfaces.bill.due_value")}
                </span>
              </div>
              <span className="flex h-11 items-center justify-center rounded-md bg-primary text-sm font-medium text-primary-foreground">
                {t("ui.landing.surfaces.bill.pay")}
              </span>
            </div>
          </div>
        </article>
      </div>
    </LandingSection>
  )
}
